import React from "react";

const TEXT_LAYOUT_OR_SIZE_CLASSES = new Set([
  "text-left",
  "text-center",
  "text-right",
  "text-justify",
  "text-start",
  "text-end",
  "text-xs",
  "text-sm",
  "text-base",
  "text-lg",
  "text-xl",
  "text-2xl",
  "text-3xl",
  "text-4xl",
  "text-5xl",
  "text-6xl",
  "text-7xl",
  "text-8xl",
  "text-9xl",
  "text-display",
  "text-page",
  "text-section",
  "text-title",
  "text-body",
  "text-control",
  "text-meta",
  "text-caption",
  "text-badge",
]);

const isTextColorClass = (rawClass) => {
  const utility = rawClass.replace(/^!/, "");
  if (utility.includes(":")) return false;
  if (!utility.startsWith("text-")) return false;
  if (TEXT_LAYOUT_OR_SIZE_CLASSES.has(utility)) return false;
  if (/^text-\[[0-9.]+(?:px|rem|em|%)\]$/.test(utility)) return false;
  return true;
};

const Button = ({
  variant = "primary",
  className = "",
  children,
  disabledReason = "",
  loading = false,
  loadingLabel,
  style,
  title,
  disabled,
  ...props
}) => {
  const isDisabled = disabled || loading;
  const hasBg = /(^|\s)!?bg-\S+(\s|$)/.test(className);
  const hasTextColor = className.split(/\s+/).some(isTextColorClass);
  const hasBorderColor = /(^|\s)!?border-\S+(\s|$)/.test(className);

  const variantStyle =
    variant === "secondary"
      ? {
          ...(!hasBg && { backgroundColor: "var(--color-bg-muted)" }),
          ...(!hasTextColor && { color: "var(--color-text)" }),
          ...(!hasBorderColor && { borderColor: "rgba(71, 71, 71, 0.18)" }),
        }
      : {
          ...(!hasBg && { backgroundColor: "var(--color-primary)" }),
          ...(!hasTextColor && { color: "#ffffff" }),
          ...(!hasBorderColor && { borderColor: "var(--color-primary)" }),
        };

  const button = (
    <button
      className={`inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-center control-text font-medium shadow-sm transition-[background-color,border-color,color,box-shadow,opacity,transform] duration-200 hover:shadow-md hover:opacity-95 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:translate-y-0 disabled:opacity-60 ${className}`}
      style={{ ...variantStyle, ...style }}
      title={title || disabledReason || undefined}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {loading && loadingLabel ? loadingLabel : children}
    </button>
  );

  if (isDisabled && disabledReason) {
    const fullWidth = /(^|\s)(w-full|flex-1)(\s|$)/.test(className);
    return (
      <span
        className={fullWidth ? "inline-flex w-full" : "inline-flex"}
        title={disabledReason}
        aria-label={disabledReason}
      >
        {button}
      </span>
    );
  }

  return button;
};

export default Button;
